/**
 * nb-free-gift.js — theme-native free-gift auto-add (replaces the "EG Auto Add to Cart" app).
 *
 * Reads window.NB_GIFT_RULES (emitted by snippets/nb-gift-rules.liquid) and reconciles the
 * cart's $0 gift lines against the market's CUMULATIVE tier ladder after every cart mutation.
 * v1.1: rules are slot-based — two gift slots (bag, carabiner), each with an ORDERED
 * variant family (primary + out-of-stock fallbacks), per-market via metaobject or code.
 *
 * Design contract (EG-FREEGIFT-NATIVE-MIGRATION-PLAN-V2.md §5 + V1.1 plan):
 *   - reactive only — never touches the shopper's own add request, so a gift failure
 *     can never break the buy button
 *   - idempotent set-diff — no-op (zero network) when the cart is already correct
 *   - gift adds batched into ONE /cart/add; on 422 each slot walks its fallback chain
 *     per-request at full quantity, then retries at reduced quantities — partial stock
 *     yields partial gifts; a slot is skipped only when every family member is at zero
 *   - server stock clamps (silent on change/update, 422 on add) are remembered per
 *     variant for the session so the reconcile converges on the achievable quantity
 *     instead of re-requesting the impossible on every cart event
 *   - once a fallback is granted it is kept for the session — no churning back to the
 *     primary mid-cart; corrections top up the family line already in the cart
 *   - quantity corrections/removals batched into ONE /cart/update.js when each variant
 *     maps to a single line; duplicate lines (e.g. EG-era lines carrying properties)
 *     fall back to per-key /cart/change and are zeroed so reconcile always converges
 *   - optimistic paint: gift rows on screen are corrected instantly from the ladder
 *     math; the authoritative server render lands right behind it
 *   - republishes cartUpdate with source 'nb-free-gift' and a full cart plus rendered
 *     sections — cart-items requires sections or it force-opens the drawer
 */
(function () {
  'use strict';

  var RULES = window.NB_GIFT_RULES;
  if (!RULES || RULES.giftRenderEnabled !== true) return;

  // The theme runtime may not exist yet when this deferred script executes: speed
  // optimizers can flip theme.js/vendor.js to async, and an early inline leaves
  // window.theme a truthy-but-empty object. A silent permanent bail here means
  // intermittent "gifts never add" page loads (caught by EU market QA) — so boot
  // when the runtime is actually ready, retrying briefly instead of giving up.
  function themeReady() {
    return !!(window.theme && window.theme.pubsub && window.theme.utils && window.theme.routes);
  }

  function boot() {
  if (window.Shopify && Shopify.designMode) return;

  // The cross-market PDP redirect used to live here, judged from RULES — removed:
  // Shopify serves shared-URL pages from cache ACROSS cookie markets, so the
  // server-rendered RULES on a product page can describe the WRONG market and this
  // check bounced shoppers into a redirect loop. The inline head script in
  // layout/theme.liquid now decides from the localization cookie alone.

  var SELF = 'nb-free-gift';
  var SLOT_KEYS = ['bag', 'carab'];

  var SLOTS = RULES.slots;
  var SPEC = RULES.tiersSpec;

  // Placeholder/skew guard: refuse to run on incomplete rules so a half-configured
  // theme (or an older snippet paired with this script) can never mutate a live cart.
  if (!SLOTS || !Array.isArray(SPEC) || !SPEC.length ||
      !Array.isArray(SLOTS.bag) || !Array.isArray(SLOTS.carab) ||
      !SLOTS.bag.length || !SLOTS.carab.length ||
      SLOTS.bag.some(function (id) { return !id; }) || SLOTS.carab.some(function (id) { return !id; })) {
    console.warn('[nb-free-gift] incomplete slots/tiersSpec in NB_GIFT_RULES — module disabled');
    return;
  }

  var QUAL = new Set(
    (RULES.qualifyingProductIds || []).concat(RULES.qualifyingFromCollection || []).filter(Boolean)
  );
  if (!QUAL.size) {
    console.warn('[nb-free-gift] no qualifying product ids in NB_GIFT_RULES — module disabled');
    return;
  }

  // Which slot each gift variant belongs to (families incl. fallbacks).
  var VARIANT_SLOT = new Map();
  SLOT_KEYS.forEach(function (k) {
    SLOTS[k].forEach(function (id) { VARIANT_SLOT.set(id, k); });
  });

  // Union of EVERY market's gift variants (code pairs + metaobject families + the
  // nb-gifts collection), not just this market's — a gift line added under another
  // market (or by the EG app pre-cutover) must be recognised and removed/adopted,
  // never double-gifted next to. Extended at load with any cart line Liquid saw
  // carrying the redirect_home tag (see cartSnapshot below).
  var ALL_GIFT_IDS = new Set(
    SLOTS.bag.concat(SLOTS.carab).concat((RULES.allGiftVariantIds || []).filter(Boolean))
  );

  // Union of every market's qualifying product ids (this market's own included).
  // A paid line in the union but NOT in this market's QUAL is a cross-market dup —
  // per config it belongs to another market, and keeping it kills the order either
  // way: restricted-catalog markets (UK/CA/SG/EU) refuse the checkout outright, and
  // even the unrestricted ones (US/International) fail later at shipping and charge
  // the wrong market's price. The config is the belonging-authority here — a live
  // catalog probe cannot be, because US/International serve EVERY product 200.
  // Two guards protect against acting on the WRONG config (see computeDiff):
  // currency mismatch (page rendered for a different market than the live cart)
  // and bfcache restores, both of which disable foreign handling for the page-view.
  var ALL_QUAL = new Set((RULES.allQualifyingProductIds || []).filter(Boolean));
  var foreignDisabled = false;
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) foreignDisabled = true; // stale page from bfcache — its QUAL may be another market's
  });

  var inFlight = false;
  var pendingCart = null;

  // Observed per-variant stock ceilings: set when the server delivers less than we
  // asked (silent clamp on change/update, quantity back-off on add), cleared whenever
  // a later request for at least the target is honored — so a mid-session restock
  // lifts the cap by itself. ponytail: page-lifetime cache, no expiry needed.
  var CLAMPED = new Map();
  function setClamp(vid, qty) { CLAMPED.set(vid, qty); }
  function clearClamp(vid) { CLAMPED.delete(vid); }
  function clampTo(vid, target) {
    var cap = CLAMPED.get(vid);
    return cap != null && cap < target ? cap : target;
  }

  // Desired quantity per slot = SUM of every reached tier's increments (CUMULATIVE,
  // merchant-confirmed; default ladder totals 1c / 1+1 / 3+3 / 5+5 / 7+7, plateau 14).
  // If the sum ever exceeds the checkout guard's cap, drop whole tiers from the top
  // until it fits — the shopper keeps up-to-cap gifts and checkout stays enabled.
  function desiredSlots(cart) {
    var qty = 0;
    cart.items.forEach(function (i) {
      if (!ALL_GIFT_IDS.has(i.variant_id) && QUAL.has(i.product_id)) qty += i.quantity;
    });

    var reached = SPEC.filter(function (t) { return qty >= t.threshold; });
    while (reached.length) {
      var want = { bag: 0, carab: 0 };
      reached.forEach(function (t) {
        want.bag += t.bag || 0;
        want.carab += t.carab || 0;
      });
      if (want.bag + want.carab <= RULES.maxFreeGiftProducts) return want;
      reached.pop();
    }
    return { bag: 0, carab: 0 };
  }

  // Read-only view for the add-to-cart toast (nb-cart-toast.js): the same qualifying
  // count, cap and ladder as above, so the toast never re-implements them. `next`
  // ignores stock clamps (CLAMPED), like the drawer banners do.
  // Bag counts that unlock a tier, lowest first, up to the checkout guard's gift cap.
  function ladderTiers() {
    var tiers = [], total = 0;
    SPEC.slice().sort(function (a, b) { return a.threshold - b.threshold; }).some(function (t) {
      total += (t.bag || 0) + (t.carab || 0);
      if (total > RULES.maxFreeGiftProducts) return true;
      // two spec entries on one threshold are one rung (ladderSteps sums them); a repeated
      // `at` would make the toast's and the drawer's fill maths divide by zero
      if (tiers.indexOf(t.threshold) < 0) tiers.push(t.threshold);
    });
    return tiers;
  }
  // Free shipping is a rung of the same ladder the site advertises (carabiner, free shipping,
  // then the gift tiers): it starts at three bags — the cart's own "Add 2 bags to receive FREE
  // shipping" copy and the PDP tip count the same way. ponytail: one number here for the
  // toast and the drawer bar; move it into NB_GIFT_RULES if it ever differs by market.
  var FREE_SHIP_BAGS = RULES.freeShippingBags || 3;

  // Every rung, lowest first: { at: bag count, bag / carab: what the rung adds, ship: free
  // shipping starts here }. A gift tier that lands on the free-shipping count is one rung.
  function ladderSteps() {
    var steps = ladderTiers().map(function (at) {
      var step = { at: at, bag: 0, carab: 0, ship: at === FREE_SHIP_BAGS };
      SPEC.forEach(function (t) {
        if (t.threshold !== at) return;
        step.bag += t.bag || 0;
        step.carab += t.carab || 0;
      });
      return step;
    });
    if (!steps.some(function (s) { return s.ship; })) steps.push({ at: FREE_SHIP_BAGS, bag: 0, carab: 0, ship: true });
    return steps.sort(function (a, b) { return a.at - b.at; });
  }
  window.theme.nbGift = {
    isGift: function (vid) { return ALL_GIFT_IDS.has(vid); },
    steps: ladderSteps, // for the cart drawer's ladder bar (nb-cart-drawer-ladder.js)
    progress: function (cart) {
      var paid = 0, gifts = 0, steps = ladderSteps();
      cart.items.forEach(function (i) {
        if (ALL_GIFT_IDS.has(i.variant_id)) gifts += i.quantity;
        else if (QUAL.has(i.product_id)) paid += i.quantity;
      });
      var want = desiredSlots(cart);
      // the very next rung, so the toast can name what it brings
      var ahead = steps.filter(function (s) { return s.at > paid; })[0];
      return {
        paid: paid, gifts: gifts, want: want.bag + want.carab, steps: steps,
        // Per slot as well as the total, so the toast can NAME the gifts it is owed
        // before the engine has finished adding them. optimisticPatch() already paints
        // the drawer rows from this same figure; the toast had nothing to paint from
        // and sat empty until the add round-trip came back.
        wantSlots: { bag: want.bag, carab: want.carab },
        next: ahead ? { need: ahead.at - paid, bag: ahead.bag, carab: ahead.carab, ship: ahead.ship } : null
      };
    }
  };

  // Cart gift lines grouped by slot; 'other' = recognised gift variants outside this
  // market's families (foreign market / EG leftovers) — always removed.
  function presentBySlot(cart) {
    var m = { bag: [], carab: [], other: [] };
    cart.items.forEach(function (i) {
      if (!ALL_GIFT_IDS.has(i.variant_id)) return;
      var slot = VARIANT_SLOT.get(i.variant_id) || 'other';
      m[slot].push({ key: i.key, quantity: i.quantity, variantId: i.variant_id });
    });
    return m;
  }

  function computeDiff(cart) {
    var want = desiredSlots(cart);
    var have = presentBySlot(cart);
    var adds = [];
    var changes = [];

    // update.js batching is only safe when a variant maps to exactly one cart line
    var linesPerVariant = {};
    cart.items.forEach(function (i) {
      if (ALL_GIFT_IDS.has(i.variant_id)) linesPerVariant[i.variant_id] = (linesPerVariant[i.variant_id] || 0) + 1;
    });
    function sole(vid) { return linesPerVariant[vid] === 1; }

    SLOT_KEYS.forEach(function (slot) {
      var lines = have[slot];
      var target = want[slot];
      if (!lines.length) {
        // Pre-clamp to a known stock ceiling so a doomed quantity is never requested.
        var addQty = clampTo(SLOTS[slot][0], target);
        want[slot] = addQty; // paint + verify against the achievable target
        if (addQty > 0) adds.push({ chain: SLOTS[slot].slice(), quantity: addQty }); // family order: primary, then fallbacks
        return;
      }
      // Keep the first family line found — once a fallback was granted, don't churn
      // back to the primary mid-session; just correct its quantity. Target is capped
      // at the variant's observed stock ceiling so the reconcile CONVERGES when the
      // gift is understocked instead of re-issuing the same clamped request forever.
      target = clampTo(lines[0].variantId, target);
      want[slot] = target;
      if (lines[0].quantity !== target) changes.push({ key: lines[0].key, quantity: target, vid: lines[0].variantId, sole: sole(lines[0].variantId) });
      lines.slice(1).forEach(function (l) { changes.push({ key: l.key, quantity: 0, vid: l.variantId, sole: false }); }); // duplicates/mixed family rows → zero to converge
    });

    have.other.forEach(function (l) {
      changes.push({ key: l.key, quantity: 0, vid: l.variantId, sole: sole(l.variantId) }); // foreign-market / EG leftovers
    });

    // Cross-market PAID lines: a per-market dup carried over from another market.
    // Removed in EVERY direction — restricted markets refuse the checkout outright,
    // and even where the catalog serves the product (US/International) the order
    // dies at shipping and carries the wrong market's price. Config is the judge;
    // the two guards make sure we never judge with another market's config:
    //   - currency: the page's rendered currency must match the live cart's — a
    //     stale/mis-cached page (all non-EU markets share the SAME urls) fails this
    //   - foreignDisabled: bfcache-restored pages sit out entirely
    var foreign = [];
    var rulesMatchCart = !foreignDisabled &&
      (!RULES.marketCurrency || !cart.currency || cart.currency === RULES.marketCurrency);
    if (rulesMatchCart) {
      cart.items.forEach(function (i) {
        if (ALL_GIFT_IDS.has(i.variant_id) || QUAL.has(i.product_id) || !ALL_QUAL.has(i.product_id)) return;
        changes.push({ key: i.key, quantity: 0, vid: i.variant_id, sole: false }); // per-key change — never batched
        foreign.push({ vid: i.variant_id, qty: i.quantity, title: i.product_title || '' });
      });
    }

    // How many items the cart badge will gain/lose once the gift lines settle —
    // lets the optimistic paint move the header count with the rows.
    var presentTotal = 0;
    ['bag', 'carab', 'other'].forEach(function (slot) {
      have[slot].forEach(function (l) { presentTotal += l.quantity; });
    });
    foreign.forEach(function (f) { presentTotal += f.qty; });

    return { adds: adds, changes: changes, want: want, countDelta: want.bag + want.carab - presentTotal, foreign: foreign };
  }

  // Optimistic paint: the ladder is deterministic, so the correct gift quantities are
  // known before the server confirms — patch the already-rendered gift rows instantly
  // (fix steppers, hide rows going to zero) and let the authoritative republish land
  // behind it. Cosmetic only: a wrong guess (e.g. out-of-stock gift) is corrected by
  // the real render a moment later. New rows (tier-up adds) still wait for the server.
  function optimisticPatch(want, countDelta, isReassert) {
    optimisticPatch._want = want; // latest targets — the delayed re-assert reads these
    try {
      document.querySelectorAll('.disable-free-item').forEach(function (row) {
        var probe = (row.getAttribute('data-handle') || '') + ' ' + row.textContent;
        var q = probe.toLowerCase().indexOf('arabiner') > -1 ? want.carab : want.bag;
        if (q === 0) { row.style.display = 'none'; return; }
        row.style.display = '';
        var input = row.querySelector('input[data-index]');
        if (input) input.value = q;
      });
      // First gift of a session: no row exists for the loop above to fast-forward, so
      // the gift would pop in only after the real render. Clone the server-rendered
      // template (nb-gift-rules.liquid) so the row appears WITH the paid item; the
      // next section render replaces the whole list with server truth. CSS order
      // classes on the template li position it (below paid items, bag above carab).
      var list = document.querySelector('#CartDrawer .horizontal-products');
      if (list) {
        [['bag', '.disable-free-item:not(.nb-gift-carab)', 'nb-gift-row-bag'],
         ['carab', '.disable-free-item.nb-gift-carab', 'nb-gift-row-carab']
        ].forEach(function (s) {
          if (!want[s[0]] || list.querySelector(s[1])) return;
          var tpl = document.getElementById(s[2]);
          if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;
          var node = tpl.content.firstElementChild.cloneNode(true);
          var input = node.querySelector('input');
          if (input) input.value = want[s[0]];
          list.appendChild(node);
        });
      }
      // The header badge counts gift items too — move it with the rows instead of
      // waiting for the authoritative render.
      if (countDelta) {
        document.querySelectorAll('cart-count').forEach(function (el) {
          var n = parseInt(el.textContent, 10);
          if (!isNaN(n)) {
            el.textContent = n + countDelta;
            if (n + countDelta > 0) el.removeAttribute('hidden');
          }
        });
      }
      // The theme's own change round-trip can swap the drawer HTML right after this
      // paint, reverting inputs to pre-gift server values until our corrective render
      // lands — re-assert the row values ONCE, shortly after the swap settles. The
      // re-assert itself must never re-arm the timer or it becomes a page-lifetime
      // 120ms loop (QA-confirmed).
      if (!isReassert && !optimisticPatch._again) {
        optimisticPatch._again = setTimeout(function () {
          optimisticPatch._again = null;
          optimisticPatch(optimisticPatch._want, 0, true);
        }, 120);
      }
    } catch (e) { /* cosmetic only — never block the reconcile */ }
  }

  function sectionIds() {
    var detail = { sections: [] };
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: detail })
    );
    if (detail.sections.length > 5) {
      console.warn('[nb-free-gift] more than 5 bundled sections; extras dropped'); // Shopify caps section rendering at 5
    }
    return detail.sections.slice(0, 5);
  }

  function freshCart() {
    return fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))
      .then(function (res) { return res.json(); });
  }

  // Checkout-settle reads bypass window.fetch entirely: app embeds wrap it (IntelliGems
  // price testing on the live theme) and can delay or reshape cart reads — the settle
  // loop must see raw server truth on a predictable clock. A hidden same-origin iframe
  // hands us an unwrapped fetch with the same cart cookies; it stays attached because
  // detaching would kill its window (and the function with it).
  var cleanFetch = null;
  function pristineFetch() {
    if (cleanFetch) return cleanFetch;
    try {
      var f = document.createElement('iframe');
      f.style.display = 'none';
      f.setAttribute('aria-hidden', 'true');
      document.documentElement.appendChild(f);
      cleanFetch = f.contentWindow.fetch.bind(f.contentWindow);
    } catch (err) {
      cleanFetch = window.fetch.bind(window); // no iframe? behave exactly as before
    }
    return cleanFetch;
  }
  function settleCartRead() {
    return pristineFetch()(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))
      .then(function (res) { return res.json(); });
  }

  async function postChange(key, quantity) {
    var res = await fetch(theme.routes.cart_change_url, Object.assign({}, theme.utils.fetchConfig(), {
      body: JSON.stringify({ id: key, quantity: quantity, sections: sectionIds() })
    }));
    if (!res.ok) throw new Error('[nb-free-gift] change failed: ' + res.status);
    return res.json(); // /cart/change returns the full cart incl. rendered sections
  }

  // ALL quantity corrections/removals in ONE round-trip; returns the full cart.
  // Only safe when each variant maps to a single cart line (updates are variant-keyed);
  // duplicate-line cleanup falls back to per-key postChange.
  async function postUpdate(updates) {
    var res = await fetch(theme.routes.cart_update_url, Object.assign({}, theme.utils.fetchConfig(), {
      // Always render sections here: if every follow-up add fails (slot fully OOS),
      // this response is what gets published — publishing without sections would
      // trigger cart-items' cart:refresh fallback, which force-opens the drawer.
      body: JSON.stringify({ updates: updates, sections: sectionIds() })
    }));
    if (!res.ok) throw new Error('[nb-free-gift] update failed: ' + res.status);
    return res.json();
  }

  async function postAdd(items) {
    var res = await fetch(theme.routes.cart_add_url, Object.assign({}, theme.utils.fetchConfig(), {
      body: JSON.stringify({
        items: items.map(function (a) { return { id: a.id, quantity: a.quantity }; }),
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    }));
    if (!res.ok) {
      // Status matters to the caller: 422/404 mean this variant CANNOT be added
      // (no stock / not sellable here), anything else is transient and must not be
      // mistaken for a permanent ceiling.
      var addErr = new Error('[nb-free-gift] add failed: ' + res.status);
      addErr.status = res.status;
      throw addErr;
    }
    return res.json(); // /cart/add returns only the added items (+ sections)
  }

  // The shopper just watched their item disappear — say why, or it reads as a bug.
  // The note sits above the drawer list, survives the section re-renders that
  // rebuild the drawer, AND survives navigations/drawer reopens (sessionStorage):
  // it retires only when the shopper edits the cart themselves — their first own
  // add/remove/qty change means they've moved on, and a lingering note about the
  // PREVIOUS market's product would read as a bug about the new one.
  var NOTE_KEY = 'nb-foreign-note-text';
  var foreignNoteText = null;
  var foreignNoteKind = null; // 'cleared' (cart was emptied) | 'removed' (items dropped, cart lives)
  try {
    var storedNote = sessionStorage.getItem(NOTE_KEY);
    if (storedNote && storedNote.charAt(0) === '{') {
      var parsedNote = JSON.parse(storedNote);
      foreignNoteText = parsedNote.t || null;
      foreignNoteKind = parsedNote.k || null;
    } else if (storedNote) {
      // pre-JSON value from a session before this format existed - never shipped
      // to shoppers, so drop it rather than paint a note of unknown kind
      sessionStorage.removeItem(NOTE_KEY);
    }
  } catch (e) {}
  function paintForeignNote() {
    if (!foreignNoteText) return;
    try {
      // An emptied cart re-renders the drawer to its EMPTY state: .drawer__empty is
      // the only visible container (the products list is gone or inside a hidden
      // wrapper), so a note anchored to the list paints invisibly — the shopper only
      // saw it during the optimistic window. Anchor to whichever state rendered.
      var empty = document.querySelector('#CartDrawer .drawer__empty');
      var list = document.querySelector('#CartDrawer .horizontal-products');
      if ((!empty && !list) || document.getElementById('nb-foreign-note')) return;
      var note = document.createElement('p');
      note.id = 'nb-foreign-note';
      note.className = 'nb-foreign-note';
      // Emails in the message become mailto links; everything else stays text
      // nodes (no innerHTML). The regex leaves a sentence-ending dot outside.
      foreignNoteText.split(/([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/).forEach(function (part) {
        if (!part) return;
        if (/^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/.test(part)) {
          var a = document.createElement('a');
          a.href = 'mailto:' + part;
          a.textContent = part;
          note.appendChild(a);
        } else {
          note.appendChild(document.createTextNode(part));
        }
      });
      if (empty) empty.insertBefore(note, empty.firstChild);
      else list.parentNode.insertBefore(note, list);
    } catch (e) { /* cosmetic only */ }
  }
  function clearForeignNote() {
    if (!foreignNoteText) return;
    foreignNoteText = null;
    foreignNoteKind = null;
    try { sessionStorage.removeItem(NOTE_KEY); } catch (e) {}
    var el = document.getElementById('nb-foreign-note');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
  function noteForeignRemoval(foreign, cleared) {
    foreign.forEach(function (f) {
      document.querySelectorAll('#CartDrawer li[data-variant-id="' + f.vid + '"]').forEach(function (row) {
        if (!row.classList.contains('disable-free-item')) row.style.display = 'none';
      });
    });
    if (cleared) {
      // The removal is about to leave the cart EMPTY — a per-item line would
      // undersell what just happened. Say it plainly and point at support.
      foreignNoteText = RULES.foreignClearedText || "Your cart was reset because your currency doesn’t match your region. Go back to www.nanobag.com and proceed with the currency where you are located to match your product to the correct fulfillment center. If you have any issues, contact us at hello@nanobag.com for assistance.";
    } else {
      var names = foreign.map(function (f) { return f.title; }).filter(Boolean).join(', ');
      foreignNoteText = (names || 'An item') + ' ' + (RULES.foreignRemovedText || 'is not available in your region and was removed so you can check out.');
    }
    foreignNoteKind = cleared ? 'cleared' : 'removed';
    try { sessionStorage.setItem(NOTE_KEY, JSON.stringify({ t: foreignNoteText, k: foreignNoteKind })); } catch (e) {}
    paintForeignNote();
  }

  // Visual checkout lock: greyed while a reconcile is in flight (the submit
  // intercept below already blocks the race — this makes it visible) and while
  // the last known cart is EMPTY (an engine-cleared cart must not offer an
  // active Checkout). Stateless re-application after every drawer re-render:
  // recomputed from current truth each time, so a stale lock cannot stick.
  // "The shopper edited the cart" cannot be inferred from event SOURCE alone —
  // the theme re-broadcasts cartUpdate after renders and drawer refreshes, and
  // those carry the SAME cart. Only a cart whose contents actually changed
  // (vs the last cart this module saw, its own mutations included) counts as a
  // shopper edit and retires the note.
  var lastCartSig = null;
  function cartSig(c) {
    try {
      return (c.items || []).map(function (i) { return i.key + ':' + i.quantity; }).join('|');
    } catch (e) { return null; }
  }

  var lastCount = null;
  function applyCheckoutLock() {
    try {
      var lock = inFlight || lastCount === 0;
      document.querySelectorAll('button[name="checkout"]').forEach(function (b) {
        b.disabled = lock;
        b.classList.toggle('nb-checkout-locked', lock);
      });
    } catch (e) { /* cosmetic only */ }
  }
  document.addEventListener('cart:updated', function () {
    setTimeout(function () { paintForeignNote(); applyCheckoutLock(); }, 0);
  });
  setTimeout(paintForeignNote, 0); // restore a persisted note on fresh page loads

  async function reconcile(cart) {
    if (inFlight) { pendingCart = cart; return; } // queue, don't drop — replayed in finally
    inFlight = true;
    lastCount = (cart && cart.item_count) || 0;
    // The cleared-note claims the cart was emptied — the first paid line the engine
    // sees makes that claim stale, whatever path added it (UI, fetch, app). The
    // 'removed' variant legitimately coexists with a healthy cart and keeps its
    // shopper-edit retirement instead.
    if (foreignNoteKind === 'cleared' && cart && (cart.items || []).some(function (i) {
      return !ALL_GIFT_IDS.has(i.variant_id) && i.price > 0;
    })) clearForeignNote();
    if (cart) lastCartSig = cartSig(cart);
    applyCheckoutLock();

    try {
      var current = cart;
      var mutated = false;
      var sections = null;
      var painted = false;
      var published = false;

      // Pass 1 mutates from the event snapshot; pass 2 verifies/repairs against the
      // authoritative post-mutation cart (stale keys, cross-tab merges, partial failures).
      // Latency: /cart/change|update already return the full cart, so change-only
      // reconciles need ZERO extra reads; adds are batched into ONE request (fallback
      // chains only when the batch 422s) plus a single /cart.js read.
      for (var pass = 0; pass < 2; pass++) {
        if (!current || !Array.isArray(current.items)) break;
        if ((current.item_count || 0) === 0) break; // never gift an empty cart

        var diff = computeDiff(current);
        if (!diff.adds.length && !diff.changes.length) break;

        if (pass === 0) {
          painted = true;
          optimisticPatch(diff.want, diff.countDelta); // instant paint; server truth lands right behind
          if (diff.foreign.length) {
            // Will this reconcile leave the cart EMPTY? Project the diff onto the
            // snapshot: lines keep their quantity unless a change overrides it,
            // plus any gift adds (an emptying cart never adds gifts).
            var nbRemaining = 0;
            current.items.forEach(function (i) {
              var ch = null;
              for (var ci = 0; ci < diff.changes.length; ci++) {
                if (diff.changes[ci].key === i.key) { ch = diff.changes[ci]; break; }
              }
              nbRemaining += ch ? ch.quantity : i.quantity;
            });
            diff.adds.forEach(function (a) { nbRemaining += a.quantity || 0; });
            noteForeignRemoval(diff.foreign, nbRemaining === 0);
          }
        }

        var passMutated = false;
        var lastFullCart = null;

        if (diff.changes.length) {
          var batchable = diff.changes.every(function (c) { return c.sole; });
          var doneBatched = false;
          if (batchable) {
            try {
              var updates = {};
              diff.changes.forEach(function (c) { updates[c.vid] = c.quantity; });
              var upd = await postUpdate(updates);
              if (upd && Array.isArray(upd.items)) {
                passMutated = true;
                lastFullCart = upd;
                sections = upd.sections || sections;
                doneBatched = true;
                // update.js clamps silently (200 + reduced quantity) — record ceilings
                diff.changes.forEach(function (c) {
                  var line = upd.items.find(function (i) { return i.variant_id === c.vid; });
                  var delivered = line ? line.quantity : 0;
                  if (delivered < c.quantity) setClamp(c.vid, delivered); else clearClamp(c.vid);
                });
              }
            } catch (e) { /* fall through to per-key */ }
          }
          if (!doneBatched) {
            for (var i = 0; i < diff.changes.length; i++) {
              try {
                var changed = await postChange(diff.changes[i].key, diff.changes[i].quantity);
                if (changed && Array.isArray(changed.items)) {
                  passMutated = true;
                  lastFullCart = changed;
                  sections = changed.sections || sections;
                  var chLine = changed.items.find(function (i2) { return i2.key === diff.changes[i].key; });
                  var chDelivered = chLine ? chLine.quantity : 0;
                  if (chDelivered < diff.changes[i].quantity) setClamp(diff.changes[i].vid, chDelivered);
                  else clearClamp(diff.changes[i].vid);
                }
              } catch (e) { /* stale key or race — the verify pass re-syncs */ }
            }
          }
        }

        if (diff.adds.length) {
          var added = null;
          try {
            // fast path: every slot's first choice in one batched request
            added = await postAdd(diff.adds.map(function (a) { return { id: a.chain[0], quantity: a.quantity }; }));
            diff.adds.forEach(function (a) { clearClamp(a.chain[0]); }); // full quantity honored
          } catch (e) {
            // batch 422'd (all-or-nothing, and 422 also means PARTIAL stock, not just
            // zero) — per slot: walk the fallback chain at full quantity first (a fully
            // stocked substitute beats a partial primary), then retry the chain at
            // reduced quantities so partial stock still yields partial gifts. A slot is
            // skipped only when every family member is at zero.
            // ponytail: 3-step back-off (want, half, 1), not exact-max discovery —
            // parsing the 422 message for the available count is locale-fragile.
            for (var j = 0; j < diff.adds.length; j++) {
              var chain = diff.adds[j].chain;
              var wantQty = diff.adds[j].quantity;
              var tries = [wantQty, Math.floor(wantQty / 2), 1].filter(function (q, idx, arr) {
                return q > 0 && arr.indexOf(q) === idx;
              });
              var slotAdded = null;
              var unsellable = true; // every failure so far was a definitive 422/404
              for (var t = 0; t < tries.length && !slotAdded; t++) {
                for (var c = 0; c < chain.length && !slotAdded; c++) {
                  try {
                    slotAdded = await postAdd([{ id: chain[c], quantity: tries[t] }]);
                    if (tries[t] < wantQty) setClamp(chain[c], tries[t]); // partial stock — remember the ceiling
                    else clearClamp(chain[c]);
                  } catch (e2) {
                    // Rate limit, 5xx or a dropped connection says nothing about stock —
                    // don't let it masquerade as an empty shelf.
                    if (e2.status !== 422 && e2.status !== 404) unsellable = false;
                  }
                }
              }
              if (slotAdded) added = slotAdded;
              else if (unsellable) {
                // The whole family is unsellable right now (every member refused even a
                // quantity of 1). Record a zero ceiling so the ladder asks for what is
                // actually achievable. Without this computeDiff keeps requesting a gift
                // that can never arrive, giftDirty never clears, and the checkout hold —
                // which deliberately has no escape hatch — traps the shopper for good.
                // Existing gift lines are unaffected: their target is clamped against
                // their own variant, not this one.
                setClamp(chain[0], 0);
              }
            }
          }
          if (added) {
            passMutated = true;
            sections = added.sections || sections;
            lastFullCart = null; // add response isn't a full cart — needs one /cart.js read
          }
        }

        if (!passMutated) break;
        mutated = true;
        current = lastFullCart || await freshCart();
      }

      if (mutated && current && Array.isArray(current.items)) {
        current.sections = sections || current.sections;
        lastCount = current.item_count || 0;
        lastCartSig = cartSig(current);
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SELF, cart: current });
        published = true;
      }
    } catch (e) {
      console.warn('[nb-free-gift] reconcile aborted:', e);
    } finally {
      if (painted && !published) {
        // Optimistic paint ran but no authoritative render followed (every mutation
        // failed, or the reconcile aborted) — without a republish the patched/hidden
        // rows would stick while the server cart still holds the gifts. A no-op
        // update returns the full cart + rendered sections to publish as truth.
        try {
          var truth = await postUpdate({});
          if (truth && Array.isArray(truth.items)) {
            lastCount = truth.item_count || 0;
            lastCartSig = cartSig(truth);
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SELF, cart: truth });
          }
        } catch (e5) { /* next cartUpdate re-syncs */ }
      }
      inFlight = false;
      applyCheckoutLock();
      if (pendingCart) {
        pendingCart = null;
        try {
          reconcile(await freshCart()); // re-check against a FRESH cart, not the stale snapshot
        } catch (e) { /* next cartUpdate re-syncs */ }
      }
    }
  }

  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
    // React to every publisher except ourselves (product-form, custom-product-atc,
    // cart-items, product-bundle, quick-order-list, gift-wrap, cart-discount, …) —
    // a denylist can't go stale when the theme gains a publisher, and the reconcile
    // is idempotent so extra triggers are free no-ops.
    if (!event || event.source === SELF) return;
    if (!event.cart || event.cart.errors || !Array.isArray(event.cart.items)) return;
    // Retire the note only on a REAL cart change — theme re-broadcasts and
    // drawer refreshes republish the same cart and must not kill it.
    var sig = cartSig(event.cart);
    if (lastCartSig !== null && sig !== null && sig !== lastCartSig) clearForeignNote();
    reconcile(event.cart);
  });

  // Checkout intercept: the Liquid checkout guard is render-time, so it can be RACED —
  // mutate the cart and click checkout before the re-render and the gift reconcile land.
  // Proven on live twice: an all-gift 14-item £0 checkout (paid removed, instant click)
  // and a 5-paid cart carrying tier-10 gift quantities (11→5 edit, instant click). At
  // submit time the LIVE cart is re-read and must be LADDER-CORRECT: an all-gift cart
  // blocks outright; wrong gift quantities hold the click while the reconcile corrects
  // them (sub-second), then the submit proceeds with the right cart. Gift test is
  // ALL_GIFT_IDS membership or unit price 0 (item.price ignores discount codes, so a
  // 100%-discounted paid line can NOT false-positive). Every failure path fails OPEN —
  // real shoppers beat throttling edge cases; direct /checkout deep links and express
  // wallet buttons stay a server-side (checkout validation function) problem.

  // A paid row's quantity INPUT disagreeing with the fetched cart means the shopper's
  // edit is still in flight server-side (type 5 over 14 → the blur fires the change →
  // the click's cart read can WIN that race and see the stale 14, which is ladder-
  // correct — proven on live). The typed value is the shopper's intent: treat the
  // mismatch as dirty and keep settling until the change lands and the ladder follows.
  // data-index is item.key on the /cart page and the 1-based line position in the
  // drawer; gift rows are skipped (locked, and optimistic paint may hold a guess).
  function paidDomDirty(c) {
    var dirty = false;
    try {
      document.querySelectorAll('input[name="updates[]"][data-index]').forEach(function (input) {
        if (dirty || input.closest('.disable-free-item')) return;
        var ref = input.getAttribute('data-index');
        var line = ref.indexOf(':') > -1
          ? c.items.find(function (i) { return i.key === ref; })
          : c.items[parseInt(ref, 10) - 1];
        if (!line) return; // stale row — the settle loop's next render refreshes it
        var val = parseInt(input.value, 10);
        if (!isNaN(val) && val !== line.quantity) dirty = true;
      });
    } catch (err) { /* best-effort — never block checkout on a DOM quirk */ }
    return dirty;
  }

  // Re-read + correct until the module is idle, the diff is empty AND the DOM agrees
  // with the server, or the deadline passes (the racy case settles in one or two
  // round-trips; the clean case costs one /cart.js read). reconcile() self-queues
  // when one is already in flight. Returns a verdict, not just the cart: at the
  // deadline the caller must know WHY we stopped — giftDirty means the gift lines
  // are provably wrong right now, domDirty means a typed paid quantity hasn't
  // landed server-side yet (so the ladder may still move).
  async function settledCart(maxMs) {
    var deadline = Date.now() + maxMs;
    for (;;) {
      var c = await settleCartRead();
      var diff = computeDiff(c);
      var giftDirty = diff.adds.length > 0 || diff.changes.length > 0;
      var domDirty = paidDomDirty(c);
      if (!inFlight && !giftDirty && !domDirty) return { cart: c, giftDirty: false, domDirty: false };
      if (Date.now() > deadline) return { cart: c, giftDirty: giftDirty, domDirty: domDirty };
      await reconcile(c);
      await new Promise(function (r) { setTimeout(r, 250); });
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.action !== 'string' || form.action.indexOf(theme.routes.cart_url) === -1) return;
    var submitter = e.submitter;
    if (!(submitter && submitter.name === 'checkout') && !form.querySelector('button[name="checkout"]')) return;
    if (form.dataset.nbGiftChecked === '1') { delete form.dataset.nbGiftChecked; return; } // our own re-submit
    if (!document.querySelector('.disable-free-item')) return; // no gift rows anywhere — nothing to race
    e.preventDefault();
    function proceed() {
      form.dataset.nbGiftChecked = '1';
      if (submitter && form.requestSubmit) { form.requestSubmit(submitter); return; }
      var hidden = document.createElement('input');
      hidden.type = 'hidden'; hidden.name = 'checkout'; hidden.value = '';
      form.appendChild(hidden);
      form.submit(); // native submit skips this handler — no recursion
    }
    settledCart(3000).then(function (r) {
      var c = r.cart;
      var allGift = Array.isArray(c.items) && c.items.length > 0 && c.items.every(function (i) {
        return ALL_GIFT_IDS.has(i.variant_id) || i.price === 0;
      });
      if (allGift) {
        console.warn('[nb-free-gift] checkout blocked: cart holds only free gifts — reconciling');
        reconcile(c); // removes the orphans and re-renders with the disabled button
        return;
      }
      // Deadline verdicts fail CLOSED, not open: a checkout created from a wrong-gift
      // cart is the one unacceptable outcome (proven reachable on live, where the
      // IntelliGems fetch wrapper can starve the settle window). Holding is cheap —
      // reconcile keeps correcting and the shopper's next click sails through.
      if (r.giftDirty) {
        console.warn('[nb-free-gift] checkout held: gift quantities still settling — reconciling');
        form.dataset.nbGiftHeld = String((parseInt(form.dataset.nbGiftHeld, 10) || 0) + 1);
        reconcile(c);
        return;
      }
      if (r.domDirty) {
        // Typed paid quantity not yet server-side — the ladder may still move. Same
        // hold, but with an escape hatch: a stuck input (DOM quirk, app-rewritten row)
        // must never trap a real shopper, so the third consecutive click goes through —
        // by then reconcile has had seconds to converge the server cart anyway.
        var held = (parseInt(form.dataset.nbGiftHeld, 10) || 0) + 1;
        if (held <= 2) {
          console.warn('[nb-free-gift] checkout held: paid quantity edit still in flight');
          form.dataset.nbGiftHeld = String(held);
          reconcile(c);
          return;
        }
      }
      delete form.dataset.nbGiftHeld;
      proceed(); // ladder-correct
    }).catch(proceed); // cart unreadable — never trap a shopper on our own failure
  }, true); // capture: run before any theme handler can act on the submit

  // On-load convergence: EG-era carts at cutover, gifts removed via full-navigation
  // links, merchant rule/cap changes, bfcache drift. The Liquid-rendered snapshot makes
  // the common already-correct case cost ZERO network; a suspected diff is verified
  // against a fresh /cart.js before any mutation (the snapshot may be cache-stale).
  (function () {
    var snap = RULES.cartSnapshot;
    if (!Array.isArray(snap) || !snap.length) return;

    var itemCount = 0;
    var items = snap.map(function (s) {
      itemCount += s.qty;
      if (s.isGift) ALL_GIFT_IDS.add(s.variantId); // adopt tag-derived gift lines missing from the config
      return { variant_id: s.variantId, product_id: s.productId, quantity: s.qty, key: s.key };
    });

    var diff = computeDiff({ items: items, item_count: itemCount });
    if (!diff.adds.length && !diff.changes.length) return;

    // The achievable quantities are known synchronously from the snapshot — paint the
    // already-rendered gift rows NOW instead of after the /cart.js round-trip. The
    // reconcile's own pass 0 re-paints from fresh truth right behind (cosmetic-only).
    optimisticPatch(diff.want, diff.countDelta);
    freshCart().then(reconcile).catch(function () { /* next cartUpdate re-syncs */ });
  })();
  } // boot()

  if (themeReady()) {
    boot();
  } else {
    var bootTries = 0;
    var bootTimer = setInterval(function () {
      if (themeReady()) {
        clearInterval(bootTimer);
        boot();
      } else if (++bootTries >= 500) { // ~20s at 40ms — runtime never appeared, give up loudly
        clearInterval(bootTimer);
        console.warn('[nb-free-gift] theme runtime never became ready — module disabled');
      }
    }, 40); // 40ms: on speed-optimizer (async theme.js) loads the engine wakes ~100ms sooner,
            // narrowing the window where a lightning add-to-cart could publish unsubscribed
  }
})();
